import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Code2, Zap, Eye, Save, FolderOpen } from 'lucide-react';
import EntityCard from '../components/builder/EntityCard';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  useSavedSchemas,
  SavedSchema
} from '../hooks/useSavedSchemas';
import { Entity, ApiInfo, CrudOptions, PropertyType } from '../types/builder';

const MAX_ENTITIES   = 20;
const MAX_PROPERTIES = 30;
const MAX_CUSTOM     = 20;

const DEFAULT_CRUD: CrudOptions = {
  getAll: true, getById: true,
  create: true, update: true, delete: true
};

const INITIAL_ENTITIES: Entity[] = [
  {
    id: 1, name: 'Product',
    expanded: true, activeTab: 'properties',
    crud: { ...DEFAULT_CRUD },
    properties: [
      { id: 1, name: 'id',          type: 'integer',  required: true,  pk: true  },
      { id: 2, name: 'productName', type: 'string',   required: true,  pk: false },
      { id: 3, name: 'price',       type: 'decimal',  required: true,  pk: false },
      { id: 4, name: 'isActive',    type: 'boolean',  required: false, pk: false },
      { id: 5, name: 'createdAt',   type: 'datetime', required: false, pk: false },
    ],
    customEndpoints: []
  }
];

// ── Helpers ───────────────────────────────────
const getBuilderType = (propDef: any): PropertyType => {
  if (propDef.format === 'date-time') return 'datetime';
  if (propDef.format === 'date')      return 'date';
  if (propDef.format === 'uuid')      return 'uuid';
  if (propDef.format === 'float' ||
      propDef.format === 'decimal')   return 'decimal';
  if (propDef.type === 'integer')     return 'integer';
  if (propDef.type === 'boolean')     return 'boolean';
  return 'string';
};

// ── Component ─────────────────────────────────
const BuilderPage = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const {
    schemas, loading: schemasLoading,
    loadSchemas, save: saveSchemaFn,
    update: updateSchemaFn,
    remove: deleteSchemaFn
  } = useSavedSchemas();

  // ── Modal states ──────────────────────────
  const [showSaveModal, setShowSaveModal]         = useState(false);
  const [showLoadModal, setShowLoadModal]         = useState(false);
  const [schemaName, setSchemaName]               = useState('');
  const [schemaDescription, setSchemaDescription] = useState('');

  // ── Schema tracking ───────────────────────
  const [loadedSchemaId, setLoadedSchemaId]     = useState<number | null>(null);
  const [loadedSchemaName, setLoadedSchemaName] = useState<string>('');

  // ── Builder state ─────────────────────────
  const [apiInfo, setApiInfo] = useState<ApiInfo>({
    title: 'My API', version: '1.0.0', basePath: '/api'
  });
  const [entities, setEntities] = useState<Entity[]>(INITIAL_ENTITIES);
  const [target, setTarget]     = useState<'dotnet' | 'java'>('dotnet');

  // ── Entity operations ─────────────────────
  const addEntity = () => {
    if (entities.length >= MAX_ENTITIES) {
      toast.error(`Maximum ${MAX_ENTITIES} entities!`);
      return;
    }
    setEntities(prev => [...prev, {
      id: Date.now(), name: 'NewEntity',
      expanded: true, activeTab: 'properties',
      crud: { ...DEFAULT_CRUD },
      properties: [{
        id: Date.now() + 1, name: 'id',
        type: 'integer', required: true, pk: true
      }],
      customEndpoints: []
    }]);
    toast.success('Entity added!');
  };

  const updateEntity = useCallback((updated: Entity) => {
    setEntities(prev =>
      prev.map(e => e.id === updated.id ? updated : e));
  }, []);

  const deleteEntity = (id: number) => {
    if (entities.length === 1) {
      toast.error('Need at least one entity!');
      return;
    }
    setEntities(prev => prev.filter(e => e.id !== id));
    toast.success('Entity deleted');
  };

  const duplicateEntity = (id: number) => {
    if (entities.length >= MAX_ENTITIES) {
      toast.error('Maximum entities reached!');
      return;
    }
    const orig = entities.find(e => e.id === id);
    if (!orig) return;
    setEntities(prev => [...prev, {
      ...JSON.parse(JSON.stringify(orig)),
      id: Date.now(),
      name: orig.name + 'Copy',
      expanded: true,
      activeTab: 'properties' as const
    }]);
    toast.success(`${orig.name} duplicated!`);
  };

  // ── Build OpenAPI ─────────────────────────
  const buildOpenApiJson = () => {
    const openApiSchemas: any = {};
    const paths: any = {};

    entities.forEach(entity => {
      if (!entity.name.trim()) return;
      const plural = entity.name.toLowerCase() + 's';
      const base   = `${apiInfo.basePath}/${plural}`;
      const props: any = {};
      const required: string[] = [];

      entity.properties.forEach(prop => {
        if (!prop.name.trim()) return;
        const typeMap: any = {
          string:   { type: 'string' },
          integer:  { type: 'integer' },
          decimal:  { type: 'number', format: 'float' },
          boolean:  { type: 'boolean' },
          date:     { type: 'string', format: 'date' },
          datetime: { type: 'string', format: 'date-time' },
          uuid:     { type: 'string', format: 'uuid' }
        };
        props[prop.name] = typeMap[prop.type] || { type: 'string' };
        if (prop.required) required.push(prop.name);
      });

      openApiSchemas[entity.name] = {
        type: 'object',
        ...(required.length > 0 && { required }),
        properties: props
      };

      const col: any  = {};
      const item: any = {};
      if (entity.crud.getAll)  col.get     = { summary: `Get all ${entity.name}s`,  tags: [entity.name] };
      if (entity.crud.create)  col.post    = { summary: `Create ${entity.name}`,     tags: [entity.name] };
      if (entity.crud.getById) item.get    = { summary: `Get ${entity.name} by ID`,  tags: [entity.name] };
      if (entity.crud.update)  item.put    = { summary: `Update ${entity.name}`,     tags: [entity.name] };
      if (entity.crud.delete)  item.delete = { summary: `Delete ${entity.name}`,     tags: [entity.name] };

      if (Object.keys(col).length  > 0) paths[base]           = col;
      if (Object.keys(item).length > 0) paths[`${base}/{id}`] = item;

      entity.customEndpoints.forEach(ep => {
        const fp = `${base}${ep.path}`;
        if (!paths[fp]) paths[fp] = {};
        paths[fp][ep.method.toLowerCase()] = {
          summary: ep.description,
          tags: [entity.name],
          'x-custom': true
        };
      });
    });

    return {
      openapi: '3.0.0',
      info: { title: apiInfo.title, version: apiInfo.version },
      components: { schemas: openApiSchemas },
      paths
    };
  };

  // ── Actions ───────────────────────────────
  const handleCopyJson = () => {
    navigator.clipboard.writeText(
      JSON.stringify(buildOpenApiJson(), null, 2));
    toast.success('OpenAPI JSON copied! 📋');
  };

  const handleGenerate = () => {
    navigate('/generator', {
      state: {
        openApiJson: JSON.stringify(buildOpenApiJson()),
        target: target === 'dotnet' ? 0 : 1
      }
    });
  };

  // ── Restore from OpenAPI ──────────────────
  const restoreFromOpenApi = (schemaJson: string): boolean => {
    try {
      const openApi = JSON.parse(schemaJson);

      if (openApi.info) {
        setApiInfo({
          title:    openApi.info.title   || 'My API',
          version:  openApi.info.version || '1.0.0',
          basePath: '/api'
        });
      }

      const oaSchemas = openApi.components?.schemas || {};
      const paths     = openApi.paths || {};

      const restored: Entity[] = Object.entries(oaSchemas)
        .map(([name, schema]: [string, any], idx) => {

          const properties = Object.entries(schema.properties || {})
            .map(([propName, propDef]: [string, any], pIdx) => ({
              id:       Date.now() + idx * 100 + pIdx,
              name:     propName,
              type:     getBuilderType(propDef),
              required: schema.required?.includes(propName) ?? false,
              pk:       propName === 'id'
            }));

          const plural     = name.toLowerCase() + 's';
          const basePath   = `/api/${plural}`;
          const pathData   = paths[basePath]           || {};
          const pathIdData = paths[`${basePath}/{id}`] || {};

          const crud: CrudOptions = {
            getAll:  !!pathData.get,
            create:  !!pathData.post,
            getById: !!pathIdData.get,
            update:  !!pathIdData.put,
            delete:  !!pathIdData.delete
          };

          const customEndpoints: any[] = [];
          Object.entries(paths).forEach(([path, methods]: [string, any]) => {
            if (!path.startsWith(basePath))  return;
            if (path === basePath)           return;
            if (path === `${basePath}/{id}`) return;
            Object.entries(methods).forEach(([method, op]: [string, any]) => {
              if (op['x-custom']) {
                customEndpoints.push({
                  id:          Date.now() + Math.random(),
                  method:      method.toUpperCase(),
                  path:        path.replace(basePath, ''),
                  description: op.summary || ''
                });
              }
            });
          });

          return {
            id: Date.now() + idx,
            name,
            expanded: true,
            activeTab: 'properties' as const,
            crud,
            properties,
            customEndpoints
          };
        });

      if (restored.length > 0) setEntities(restored);
      return true;

    } catch (err) {
      console.error(err);
      toast.error('Failed to restore schema!');
      return false;
    }
  };

  // ── Save / Update ─────────────────────────
  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Please login!');
      return;
    }

    const openApiJson = buildOpenApiJson();

    try {
      if (loadedSchemaId) {
        // UPDATE existing schema ✅
        await updateSchemaFn(loadedSchemaId, {
          name:        loadedSchemaName,
          description: schemaDescription,
          schemaJson:  JSON.stringify(openApiJson, null, 2),
          target:      target === 'dotnet' ? 0 : 1
        });
        toast.success('Schema updated! ✅');

      } else {
        // SAVE new schema ✅
        if (!schemaName.trim()) {
          toast.error('Enter a name!');
          return;
        }
        const saved = await saveSchemaFn({
          name:        schemaName,
          description: schemaDescription,
          schemaJson:  JSON.stringify(openApiJson, null, 2),
          target:      target === 'dotnet' ? 0 : 1
        });
        setSchemaName('');
        setSchemaDescription('');
        setShowSaveModal(false);
        if (saved?.id) {
          setLoadedSchemaId(saved.id);
          setLoadedSchemaName(schemaName);
        }
      }
    } catch { /* handled in hook */ }
  };

  // ── Load ──────────────────────────────────
  const handleLoadSchema = async (schema: SavedSchema) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://api.scaffoldbridge.io"
        }
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5062'}/api/schema/${schema.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const rawJson: string = response.data.schemaJson;

      try {
        JSON.parse(rawJson);
        const ok = restoreFromOpenApi(rawJson);
        if (ok) {
          setTarget(schema.target === 1 ? 'java' : 'dotnet');
          setLoadedSchemaId(schema.id);
          setLoadedSchemaName(schema.name);
          toast.success(`Loaded: ${schema.name} ✅`);
          setShowLoadModal(false);
        }
      } catch {
        toast.error('SQL schemas cannot load in Builder. Use Generator page!');
      }

    } catch {
      toast.error('Failed to load schema!');
    }
  };

  // ── Stats ─────────────────────────────────
  const hasPkConflict = entities.some(
    e => e.properties.filter(p => p.pk).length > 1);
  const totalProps  = entities.reduce((a, e) => a + e.properties.length, 0);
  const totalCustom = entities.reduce((a, e) => a + e.customEndpoints.length, 0);
  const totalCrud   = entities.reduce(
    (a, e) => a + Object.values(e.crud).filter(Boolean).length, 0);

  // ── Render ────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-5xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="text-blue-400 font-mono text-sm mb-3">
            // Visual API Builder
          </div>
          <h1 className="text-4xl font-bold text-blue-200 mb-3">
            Build Your API Visually
          </h1>
          <p className="text-gray-500">
            No OpenAPI knowledge needed! Add entities and generate instantly.
          </p>
        </div>

        {/* API Info */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Code2 className="w-4 h-4 text-blue-400" />
            <h2 className="text-blue-200 font-semibold">API Information</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'API Title', key: 'title',    placeholder: 'My API' },
              { label: 'Version',   key: 'version',  placeholder: '1.0.0' },
              { label: 'Base Path', key: 'basePath', placeholder: '/api' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-gray-500 text-xs font-mono uppercase tracking-wider mb-2">
                  {f.label}
                </label>
                <input
                  value={apiInfo[f.key as keyof ApiInfo]}
                  onChange={e => setApiInfo({ ...apiInfo, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-200
                             rounded-xl px-4 py-2.5 text-sm font-mono outline-none
                             focus:border-blue-500 transition-colors"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Target Framework */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Code2 className="w-4 h-4 text-blue-400" />
            <h2 className="text-blue-200 font-semibold">Target Framework</h2>
          </div>
          <div className="flex gap-3">
            {(['dotnet', 'java'] as const).map(t => (
              <button key={t} onClick={() => setTarget(t)}
                className={`flex-1 py-3 rounded-xl text-sm font-semibold border transition-colors ${
                  target === t
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                }`}>
                {t === 'dotnet' ? '.NET 8 Web API' : '☕ Java Spring Boot (Maven)'}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Entities',         value: entities.length },
            { label: 'Properties',       value: totalProps },
            { label: 'CRUD Endpoints',   value: totalCrud },
            { label: 'Custom Endpoints', value: totalCustom },
          ].map(s => (
            <div key={s.label}
                 className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-400 mb-1">{s.value}</div>
              <div className="text-gray-600 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Entity Cards */}
        {entities.map(entity => (
          <EntityCard
            key={entity.id}
            entity={entity}
            onUpdate={updateEntity}
            onDelete={() => deleteEntity(entity.id)}
            onDuplicate={() => duplicateEntity(entity.id)}
            maxProperties={MAX_PROPERTIES}
            maxCustomEndpoints={MAX_CUSTOM}
            target={target}
          />
        ))}

        {/* Add Entity */}
        <button
          onClick={addEntity}
          disabled={entities.length >= MAX_ENTITIES}
          className={`w-full border-2 border-dashed rounded-2xl py-5 text-sm
                      font-medium transition-colors mb-8 flex items-center justify-center gap-2 ${
            entities.length >= MAX_ENTITIES
              ? 'border-gray-800 text-gray-700 cursor-not-allowed'
              : 'border-gray-800 hover:border-blue-600 text-gray-600 hover:text-blue-400'
          }`}>
          {entities.length >= MAX_ENTITIES
            ? `Maximum entities reached (${MAX_ENTITIES}/${MAX_ENTITIES})`
            : `+ Add Entity (${entities.length}/${MAX_ENTITIES})`}
        </button>

        {/* Action Buttons */}
        <div className="flex gap-4 flex-wrap mb-8">

          {isAuthenticated && (
            <>
              {/* Save / Update toggle */}
              {loadedSchemaId ? (
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 bg-green-700 hover:bg-green-600
                             text-white border border-green-600 px-4 py-4
                             rounded-xl text-sm font-medium transition-colors">
                  <Save className="w-4 h-4" />
                  Update Schema
                  <span className="text-green-300 text-xs font-mono ml-1 truncate max-w-24">
                    ({loadedSchemaName})
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700
                             text-gray-300 border border-gray-700 px-4 py-4
                             rounded-xl text-sm font-medium transition-colors">
                  <Save className="w-4 h-4" />
                  Save Schema
                </button>
              )}

              {/* Load */}
              <button
                onClick={() => {
                  loadSchemas();
                  setShowLoadModal(true);
                }}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700
                           text-gray-300 border border-gray-700 px-4 py-4
                           rounded-xl text-sm font-medium transition-colors">
                <FolderOpen className="w-4 h-4" />
                Load Schema
              </button>

              {/* New Schema (only when loaded) */}
              {loadedSchemaId && (
                <button
                  onClick={() => {
                    setEntities(INITIAL_ENTITIES);
                    setApiInfo({ title: 'My API', version: '1.0.0', basePath: '/api' });
                    setTarget('dotnet');
                    setLoadedSchemaId(null);
                    setLoadedSchemaName('');
                    toast.success('Started new schema! ✅');
                  }}
                  className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700
                             text-gray-400 border border-gray-700 px-4 py-4
                             rounded-xl text-sm font-medium transition-colors">
                  + New Schema
                </button>
              )}
            </>
          )}

          {/* Copy JSON */}
          <button
            onClick={handleCopyJson}
            className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700
                       text-gray-300 font-semibold px-6 py-4 rounded-xl border border-gray-700
                       transition-colors flex-1">
            <Eye className="w-4 h-4" />
            Copy OpenAPI JSON
          </button>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={hasPkConflict}
            className={`flex items-center justify-center gap-2 font-bold px-10 py-4
                        rounded-xl transition-all flex-1 ${
              hasPkConflict
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500 text-white hover:shadow-lg hover:shadow-blue-500/25'
            }`}>
            <Zap className="w-5 h-5" />
            {hasPkConflict ? '⚠️ Fix PK Conflicts First' : 'Generate Project →'}
          </button>
        </div>

      </div>
      {/* ↑ End of max-w-5xl div */}

      {/* ── Save Modal ── OUTSIDE inner div ── */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
                        flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-blue-200 font-semibold text-lg mb-4">
              💾 Save Schema
            </h3>

            <div className="mb-4">
              <label className="block text-gray-400 text-xs font-mono mb-2">
                Schema Name *
              </label>
              <input
                type="text"
                value={schemaName}
                onChange={e => setSchemaName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                placeholder="My API Schema"
                autoFocus
                className="w-full bg-gray-800 border border-gray-700 text-gray-200
                           rounded-xl px-4 py-3 text-sm outline-none
                           focus:border-blue-500 transition-colors"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-400 text-xs font-mono mb-2">
                Description (optional)
              </label>
              <input
                type="text"
                value={schemaDescription}
                onChange={e => setSchemaDescription(e.target.value)}
                placeholder="Products and orders API"
                className="w-full bg-gray-800 border border-gray-700 text-gray-200
                           rounded-xl px-4 py-3 text-sm outline-none
                           focus:border-blue-500 transition-colors"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSchemaName('');
                  setSchemaDescription('');
                }}
                className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-700
                           text-gray-400 border border-gray-700 transition-colors text-sm">
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-500
                           text-white font-semibold transition-colors text-sm
                           flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                Save Schema
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Load Modal ── OUTSIDE inner div ── */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
                        flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg">
            <h3 className="text-blue-200 font-semibold text-lg mb-4">
              📂 Load Schema
            </h3>

            {schemasLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500
                                border-t-transparent rounded-full"/>
              </div>
            ) : schemas.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No saved schemas yet!</p>
                <p className="text-gray-600 text-xs mt-2">Save a schema first.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
                {schemas.map(schema => {
                  // Use inputType from API ✅
                  const isSql = schema.inputType === 'sql';

                  return (
                    <div
                      key={schema.id}
                      onClick={() => !isSql && handleLoadSchema(schema)}
                      className={`flex items-center justify-between bg-gray-800
                                 border rounded-xl p-4 transition-colors ${
                        isSql
                          ? 'border-gray-700 opacity-50 cursor-not-allowed'
                          : 'border-gray-700 hover:border-blue-500/50 cursor-pointer'
                      }`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-gray-200 text-sm font-medium truncate">
                            {schema.name}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                            isSql
                              ? 'bg-orange-500/20 text-orange-400'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {isSql ? 'SQL' : 'OpenAPI'}
                          </span>
                        </div>
                        {schema.description && (
                          <p className="text-gray-500 text-xs mt-0.5 truncate">
                            {schema.description}
                          </p>
                        )}
                        <p className="text-gray-600 text-xs mt-1">
                          {schema.target === 0 ? '.NET 8' : 'Java Spring Boot'}
                          {' · '}
                          {new Date(schema.updatedAt).toLocaleDateString()}
                          {isSql && ' · Use Generator page'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        {!isSql && (
                          <button
                            onClick={e => { e.stopPropagation(); handleLoadSchema(schema); }}
                            className="text-blue-400 hover:text-blue-300 text-xs px-3 py-1.5
                                       rounded-lg bg-blue-500/10 hover:bg-blue-500/20 transition-colors">
                            Load
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); deleteSchemaFn(schema.id); }}
                          className="text-red-400 hover:text-red-300 text-xs px-3 py-1.5
                                     rounded-lg bg-red-500/10 hover:bg-red-500/20 transition-colors">
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setShowLoadModal(false)}
              className="w-full py-3 rounded-xl bg-gray-800 hover:bg-gray-700
                         text-gray-400 border border-gray-700 transition-colors text-sm">
              Close
            </button>
          </div>
        </div>
      )}

    </div>
    // ↑ End of min-h-screen div
  );
};

export default BuilderPage;